import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import {
  Download,
  InsertDriveFile,
  PictureAsPdf,
  Image,
  Description,
  TableChart,
  FolderZip
} from '@mui/icons-material';
import { MailAttachment } from '../types';
import { formatFileSize } from '../utils';
import { getAttachmentDownloadUrl } from '../api';

interface MailAttachmentItemProps {
  attachment: MailAttachment;
  username: string;
  messageId: string;
}

export const MailAttachmentItem: React.FC<MailAttachmentItemProps> = ({
  attachment,
  username,
  messageId
}) => {
  const getFileIcon = () => {
    const ext = (attachment.filename.split('.').pop() || '').toLowerCase();
    const ct = (attachment.contentType || '').toLowerCase();

    if (ext === 'pdf' || ct.includes('pdf')) {
      return <PictureAsPdf sx={{ color: '#ef4444', fontSize: 22 }} />;
    }
    if (['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext) || ct.includes('image')) {
      return <Image sx={{ color: '#0ea5e9', fontSize: 22 }} />;
    }
    if (['doc', 'docx'].includes(ext) || ct.includes('word')) {
      return <Description sx={{ color: '#2563eb', fontSize: 22 }} />;
    }
    if (['xls', 'xlsx', 'csv'].includes(ext) || ct.includes('sheet') || ct.includes('excel')) {
      return <TableChart sx={{ color: '#16a34a', fontSize: 22 }} />;
    }
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext) || ct.includes('zip')) {
      return <FolderZip sx={{ color: '#d97706', fontSize: 22 }} />;
    }
    return <InsertDriveFile sx={{ color: '#64748b', fontSize: 22 }} />;
  };

  const handleDownload = () => {
    const url = getAttachmentDownloadUrl(
      username,
      messageId,
      attachment.part,
      attachment.filename
    );
    window.open(url, '_blank');
  };

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: '12px',
        bgcolor: '#f8fafc',
        border: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        maxWidth: 320,
        transition: 'all 0.15s ease',
        '&:hover': {
          bgcolor: '#f1f5f9',
          borderColor: '#cbd5e1'
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
        {getFileIcon()}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            title={attachment.filename}
            sx={{
              fontWeight: 600,
              fontSize: '0.84rem',
              color: '#1e293b',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {attachment.filename}
          </Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.72rem' }}>
            {formatFileSize(attachment.size)}
          </Typography>
        </Box>
      </Box>

      <Button
        size="small"
        variant="text"
        onClick={handleDownload}
        sx={{
          minWidth: 32,
          p: 0.5,
          color: '#2563eb',
          '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.1)' }
        }}
      >
        <Download sx={{ fontSize: 18 }} />
      </Button>
    </Box>
  );
};

export default MailAttachmentItem;
